import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, CheckCircle, Clock, FileText, AlertCircle, BarChart3 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { StatCard } from './components/StatCard';
import { AppointmentCard } from '../appointments/AppointmentCard';
import AppointmentDetails from '../appointments/AppointmentDetails';
import { DashboardEventBanner } from '../announcements';

export const CounselorDashboard = () => {
  const navigate = useNavigate();
  const { get, post, put } = useApi();
  const { user } = useAuth();
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({
    todayAppointments: 0,
    activeClients: 0,
    hoursScheduled: 0,
    pendingNotes: 0,
    todaySchedule: [],
    pendingActions: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await get('/appointments');
      
      if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
        setAppointments(response.data.data);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await get('/dashboard/stats/counselor');
      if (response.data?.status === 'success') {
        setStats(response.data.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const response = await put(`/appointments/${appointmentId}/status`, {
        status: newStatus
      });
      
      if (response.data?.status === 'success') {
        if (newStatus === 'in-progress') {
          navigate(`/counselor/sessions/${appointmentId}`);
        } else {
          fetchStats();
        }
      }
    } catch (error) {
      console.error('Failed to update appointment status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-usiu-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 rounded-r-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 bg-clip-text text-transparent">
          Welcome back, {user?.profile?.fullName || user?.fullName || user?.username}!
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Here's your schedule and client overview for today</p>
      </div>

      {/* Event Announcements Banner */}
      <DashboardEventBanner />

      {/* Quick Actions Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          onClick={() => navigate('/counselor/appointments')}
          className="group relative overflow-hidden bg-gradient-to-br from-usiu-blue-500 to-usiu-blue-600 hover:from-usiu-blue-600 hover:to-usiu-blue-700 text-white rounded-xl p-3 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-2xl backdrop-blur-sm">
              <Calendar className="h-5 w-5" />
            </div>
            <div className="text-left flex-1">
              <div className="font-bold text-sm">All Appointments</div>
              <div className="text-[11px] text-blue-100 mt-0.5 opacity-80">View schedule</div>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/counselor/clients')}
          className="group relative overflow-hidden bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl p-3 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-2xl backdrop-blur-sm">
              <Users className="h-5 w-5" />
            </div>
            <div className="text-left flex-1">
              <div className="font-bold text-sm">My Clients</div>
              <div className="text-[11px] text-purple-100 mt-0.5 opacity-80">Manage clients</div>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/counselor/sessions')}
          className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl p-3 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-2xl backdrop-blur-sm">
              <FileText className="h-5 w-5" />
            </div>
            <div className="text-left flex-1">
              <div className="font-bold text-sm">Session Notes</div>
              <div className="text-[11px] text-emerald-100 mt-0.5 opacity-80">View all sessions</div>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/counselor/analytics')}
          className="group relative overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl p-3 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-2xl backdrop-blur-sm">
              <BarChart3 className="h-5 w-5" />
            </div>
            <div className="text-left flex-1">
              <div className="font-bold text-sm">Analytics</div>
              <div className="text-[11px] text-orange-100 mt-0.5 opacity-80">View insights</div>
            </div>
          </div>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.todayAppointments || 0}</div>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Today's Appointments</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Scheduled for today</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-2xl">
              <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeClients || 0}</div>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Active Clients</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Currently in counseling</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
              <Clock className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.hoursScheduled || 0}</div>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Hours Scheduled</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total hours today</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-2xl">
              <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pendingNotes || 0}</div>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Pending Notes</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Require attention</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Schedule */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-md transition-all duration-300">
          <div className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Today's Schedule
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {stats.todaySchedule?.length || 0} appointment{stats.todaySchedule?.length !== 1 ? 's' : ''} scheduled
            </p>
          </div>
          <div className="p-6 max-h-[600px] overflow-y-auto">
            {stats.todaySchedule?.length > 0 ? (
              <div className="space-y-3">
                {stats.todaySchedule.map((appointment) => (
                  <AppointmentCard
                    key={appointment._id}
                    appointment={appointment}
                    onViewDetails={() => setSelectedAppointmentId(appointment._id)}
                    onStatusChange={(status) => handleStatusChange(appointment._id, status)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full mb-4">
                  <Calendar className="h-10 w-10 text-gray-400 dark:text-gray-600" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">No appointments today</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Your schedule is clear for the day</p>
              </div>
            )}
          </div>
        </div>

        {/* Pending Actions */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-md transition-all duration-300">
          <div className="bg-gradient-to-r from-orange-50/50 to-amber-50/50 dark:from-orange-900/10 dark:to-amber-900/10 px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              Pending Actions
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {stats.pendingActions?.length || 0} item{stats.pendingActions?.length !== 1 ? 's' : ''} require{stats.pendingActions?.length === 1 ? 's' : ''} attention
            </p>
          </div>
          <div className="p-6 max-h-[600px] overflow-y-auto">
            {stats.pendingActions?.length > 0 ? (
              <div className="space-y-3">
                {stats.pendingActions.map((action, index) => (
                  <div
                    key={index}
                    className="group flex items-start gap-4 p-4 bg-gradient-to-r from-yellow-50/50 to-orange-50/50 dark:from-yellow-900/10 dark:to-orange-900/10 hover:from-yellow-100/50 hover:to-orange-100/50 rounded-xl border border-yellow-200/50 dark:border-yellow-900/30 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-2xl">
                        <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-relaxed">{action}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-50 dark:bg-green-900/20 rounded-full mb-4">
                  <CheckCircle className="h-10 w-10 text-green-500 dark:text-green-400" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">All caught up!</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">No pending actions at this time</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedAppointmentId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-200 dark:border-gray-800 animate-in zoom-in-95 duration-300">
            <AppointmentDetails
              appointmentId={selectedAppointmentId}
              onClose={() => {
                setSelectedAppointmentId(null);
                fetchAppointments();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CounselorDashboard;